import { zeroErrorMessage } from "@/lib/zero-error";
import { useMutation } from "@tanstack/react-query";
import { useQuery as useZeroQuery } from "@rocicorp/zero/react";
import { appQueries } from "@production-hub/shared";
import { toast } from "sonner";

export interface CrewEmailRecipient {
  id: string;
  crew_email_id: string;
  person_id: string;
  email: string;
  sent_at: string;
  person?: {
    id: string;
    first_name: string;
    last_name: string;
  };
}

export interface CrewEmailAttachment {
  id: string;
  crew_email_id: string;
  filename: string;
  content_type: string;
  size_bytes: number;
}

export interface CrewEmail {
  id: string;
  project_id: string;
  subject: string;
  html_body: string;
  sent_by: string | null;
  status: "draft" | "sent";
  draft_person_ids: string[];
  created_at: string;
  updated_at: string;
  recipients?: CrewEmailRecipient[];
  attachments?: CrewEmailAttachment[];
}

type ZeroCrewEmail = {
  id: string;
  projectID: string;
  subject: string;
  htmlBody: string;
  sentBy?: string | null;
  status: "draft" | "sent";
  draftPersonIds?: string[] | null;
  createdAt?: number | string | null;
  updatedAt?: number | string | null;
  recipients?: {
    id: string;
    crewEmailID: string;
    personID: string;
    email: string;
    sentAt?: number | string | null;
    person?: {
      id: string;
      firstName: string;
      lastName: string;
    } | null;
  }[];
  attachments?: {
    id: string;
    crewEmailID: string;
    filename: string;
    contentType: string;
    sizeBytes: number;
  }[];
};

function toLegacyTimestamp(value: number | string | null | undefined) {
  if (typeof value === "number") return new Date(value).toISOString();
  return value ?? "";
}

function toLegacyCrewEmail(row: ZeroCrewEmail): CrewEmail {
  const createdAt = toLegacyTimestamp(row.createdAt);
  return {
    id: row.id,
    project_id: row.projectID,
    subject: row.subject,
    html_body: row.htmlBody,
    sent_by: row.sentBy ?? null,
    status: row.status,
    draft_person_ids: row.draftPersonIds ?? [],
    created_at: createdAt,
    updated_at: toLegacyTimestamp(row.updatedAt) || createdAt,
    recipients:
      row.recipients?.map((recipient) => ({
        id: recipient.id,
        crew_email_id: recipient.crewEmailID,
        person_id: recipient.personID,
        email: recipient.email,
        sent_at: toLegacyTimestamp(recipient.sentAt),
        person: recipient.person
          ? {
              id: recipient.person.id,
              first_name: recipient.person.firstName,
              last_name: recipient.person.lastName,
            }
          : undefined,
      })) ?? [],
    attachments:
      row.attachments?.map((attachment) => ({
        id: attachment.id,
        crew_email_id: attachment.crewEmailID,
        filename: attachment.filename,
        content_type: attachment.contentType,
        size_bytes: attachment.sizeBytes,
      })) ?? [],
  };
}

export function useCrewEmails(projectId: string | undefined) {
  const query = projectId
    ? appQueries.crewEmails.byProject({ projectID: projectId })
    : undefined;
  const [data, details] = useZeroQuery(query, { enabled: !!projectId });
  const emails = ((data ?? []) as ZeroCrewEmail[]).map(toLegacyCrewEmail);

  return {
    data: emails,
    isLoading: details.type !== "complete" && emails.length === 0,
    isError: details.type === "error",
    error:
      details.type === "error"
        ? new Error(zeroErrorMessage(details.error))
        : null,
  };
}

interface SendCrewEmailParams {
  draft_id?: string;
  project_id: string;
  subject: string;
  html_body: string;
  person_ids: string[];
  files: File[];
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      // Strip the data URL prefix (e.g., "data:application/pdf;base64,")
      const base64 = result.split(",")[1];
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export function useSendCrewEmail() {
  return useMutation({
    mutationFn: async ({
      draft_id,
      project_id,
      subject,
      html_body,
      person_ids,
      files,
    }: SendCrewEmailParams) => {
      const attachments = await Promise.all(
        files.map(async (file) => ({
          filename: file.name,
          content_base64: await fileToBase64(file),
          content_type: file.type || "application/octet-stream",
          size_bytes: file.size,
        })),
      );

      const res = await fetch("/api/crew-email/send", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          draft_id,
          project_id,
          subject,
          html_body,
          person_ids,
          attachments,
        }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: res.statusText }));
        throw new Error(err.error || "Failed to send email");
      }

      return res.json();
    },
    onSuccess: (_, variables) => {
      toast.success(
        `Email sent to ${variables.person_ids.length} recipient(s)`,
      );
    },
    onError: (error) => {
      toast.error("Failed to send email: " + error.message);
    },
  });
}

interface SaveDraftParams {
  id?: string;
  project_id: string;
  subject: string;
  html_body: string;
  person_ids: string[];
}

export function useSaveDraft() {
  return useMutation({
    mutationFn: async (params: SaveDraftParams) => {
      const res = await fetch("/api/crew-email/draft", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(params),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: res.statusText }));
        throw new Error(err.error || "Failed to save draft");
      }

      return res.json();
    },
    onSuccess: () => {
      toast.success("Draft saved");
    },
    onError: (error) => {
      toast.error("Failed to save draft: " + error.message);
    },
  });
}

export function useDeleteDraft() {
  return useMutation({
    mutationFn: async ({ id }: { id: string; project_id: string }) => {
      const res = await fetch(`/api/crew-email/draft/${id}`, {
        method: "DELETE",
        credentials: "include",
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: res.statusText }));
        throw new Error(err.error || "Failed to delete draft");
      }

      return res.json();
    },
    onSuccess: () => {
      toast.success("Draft deleted");
    },
    onError: (error) => {
      toast.error("Failed to delete draft: " + error.message);
    },
  });
}
